import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Define the user interface
export interface User {
  _id?: string;
  fullName?: string;
  universityEmail?: string;
  institutionName?: string;
  academicType?: string;
  orcidId?: string;
  openAlexId?: string;
  password?: string;
  pfp?: string;
  isVerified?: boolean;
  __v?: number;
}

// Define the AuthContext type
interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  setUser: (user: User | null) => void;
  setAuth: (value: boolean) => void;
  loading: boolean; // ✅ NEW: Add loading state
}

// Create the AuthContext
export const AuthContext = createContext<AuthContextType | null>(null);

// Provider Component
const AuthContextProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true); // ✅ NEW: Add loading state

  const setAuth = (value: boolean) => {
    setIsAuthenticated(value);
  };

  const checkAuth = async () => {
    setLoading(true); // Start loading before API call
    try {
      const response = await fetch('https://academichub-backend.onrender.com/api/auth/check', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Authentication failed');
      }

      const data = await response.json();
      console.log('Auth Check Response:', data);

      setAuth(data.isAuthenticated);
      setUser(data.user || null);
    } catch (error) {
      console.error('Auth check failed:', error);
      setAuth(false);
      setUser(null);
    } finally {
      setLoading(false); // ✅ Done loading
    }
  };

  // Check authentication on mount and if isAuthenticated changes
  useEffect(() => {
    checkAuth();
  }, [isAuthenticated]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, setUser, setAuth, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthContextProvider');
  }
  return context;
};

export default AuthContextProvider;
